/* eslint-disable max-lines */
import {
  SEPARATOR,
  useCameras,
  useDeleteCameraGroup,
} from '@hakimo-ui/hakimo/data-access';
import { CameraGroupDetail } from '@hakimo-ui/hakimo/types';
import { BasicPanel } from '@hakimo-ui/hakimo/ui-elements';
import { toast, useCanDeleteCameraGroup } from '@hakimo-ui/hakimo/util';
import { Button, Modal } from '@hakimo-ui/shared/ui-base';
import { useMemo, useState } from 'react';
import DraggableCameraItem from './cards/DraggableCameraItem';

interface Props {
  group: CameraGroupDetail;
  onDeleted?: () => void;
}

function CameraGroupOverview({ group, onDeleted }: Props) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const hasCameras = group.cameras.length > 0;
  const canDeleteCameraGroup = useCanDeleteCameraGroup();

  const cameraIds = useMemo(
    () => group.cameras.map((c) => c.id),
    [group.cameras]
  );

  const { data: camerasData } = useCameras(
    cameraIds.length
      ? new URLSearchParams({
          ids: cameraIds.join(SEPARATOR),
          includeImage: 'true',
        }).toString()
      : '',
    cameraIds.length > 0
  );

  const cameraMap = useMemo(
    () => new Map(camerasData?.items.map((c) => [c.id, c]) ?? []),
    [camerasData]
  );

  const onSuccess = () => {
    toast('Camera Group Deleted', { type: 'success' });
    onDeleted?.();
  };

  const deleteCameraGroupMutation = useDeleteCameraGroup(
    group.cameraGroupId,
    onSuccess
  );

  const onSubmit = () => {
    deleteCameraGroupMutation.mutate({});
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col overflow-hidden rounded-xl">
        <div className="dark:bg-ondark-bg-1 bg-onlight-bg-1 space-y-6 px-6 py-4">
          <div>
            <p className="dark:text-dark-secondary-text text-onlight-text-3">
              Location
            </p>
            <p className="dark:text-dark-text text-onlight-text-1 mt-1">
              {group.locationName}
            </p>
          </div>

          <div>
            <p className="dark:text-dark-secondary-text text-onlight-text-3">
              Name
            </p>
            <p className="dark:text-dark-text text-onlight-text-1 mt-1">
              {group.name}
            </p>
          </div>

          <div>
            <p className="dark:text-dark-secondary-text text-onlight-text-3">
              Description
            </p>
            <p className="dark:text-dark-text text-onlight-text-1 mt-1">
              {group.description}
            </p>
          </div>
        </div>
      </div>

      <BasicPanel
        title="Cameras"
        secondary={
          <span className="text-onlight-text-3 dark:text-ondark-text-2 text-sm">
            {group.cameras.length} cameras
          </span>
        }
      >
        <div className="flex flex-col pb-2">
          {group.cameras.map((cam) => {
            const fullCam = cameraMap.get(cam.id);
            return (
              <DraggableCameraItem
                key={cam.id}
                cam={cam}
                id={cam.id}
                name={cam.name}
                imageUrl={fullCam?.imageUrl}
                livestreamUrl={fullCam?.livestreamUrl}
                className="px-6 font-medium"
              />
            );
          })}
        </div>
      </BasicPanel>

      {canDeleteCameraGroup && (
        <BasicPanel title="Delete">
          <div className="px-6 py-4">
            <p className="text-onlight-text-2 dark:text-dark-text mb-4 text-sm">
              No longer want to use this camera group for monitoring? You can
              delete the group here.
            </p>
            <Button
              variant="error"
              onClick={() => {
                if (hasCameras) {
                  toast('Move cameras to another group before deleting.', {
                    type: 'error',
                  });
                  return;
                }
                setShowConfirmModal(true);
              }}
              disabled={deleteCameraGroupMutation.isLoading}
            >
              Delete Group
            </Button>
          </div>
        </BasicPanel>
      )}

      <Modal
        title="Confirmation"
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="error"
              onClick={onSubmit}
              loading={deleteCameraGroupMutation.isLoading}
            >
              Confirm
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div className="w-96 px-8 py-4 text-sm">
          Do you really want to delete this Camera Group?
        </div>
      </Modal>
    </div>
  );
}

export default CameraGroupOverview;
